import { useEffect, useState, useCallback, useRef } from "react";
import "./scrollAnimation.css";

/**
 * Custom hook for smooth & slow scroll-in animations (no scroll out).
 * Supports dynamically/asynchronously rendered elements.
 * @param {number} threshold - IntersectionObserver threshold (default: 0.15)
 * @param {string} rootMargin - Observer root margin (default: "0px")
 * @param {boolean} triggerOnce - Keep visible after first scroll in (default: true)
 * @returns {[Function, boolean]} [ref, isVisible]
 */
export const useScrollAnimation = (threshold = 0.15, rootMargin = "0px", triggerOnce = true) => {
  const [node, setNode] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const refObj = useRef(null);

  const refCallback = useCallback((element) => {
    refObj.current = element;
    setNode(element);
  }, []);

  Object.defineProperty(refCallback, 'current', {
    get() {
      return refObj.current;
    },
    set(val) {
      refObj.current = val;
      setNode(val);
    },
    configurable: true,
  });

  useEffect(() => {
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(node);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(node);

    return () => {
      if (node) {
        observer.unobserve(node);
      }
      observer.disconnect();
    };
  }, [node, threshold, rootMargin, triggerOnce]);

  return [refCallback, isVisible];
};

export default useScrollAnimation;
